/**
 * Protected route component.
 *
 * Wraps routes that require authentication.
 * Redirects to login if not authenticated.
 */

import React from 'react';
import { useAuth } from './useAuth';
import { Spinner } from '../components/ui';

interface RequireAuthProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

/**
 * Wrapper component that requires authentication.
 *
 * Usage:
 * ```tsx
 * <Route path="/protected" element={
 *   <RequireAuth>
 *     <ProtectedPage />
 *   </RequireAuth>
 * } />
 * ```
 */
export function RequireAuth({
  children,
  requireAdmin = false,
}: RequireAuthProps) {
  const { isAuthenticated, isLoading, user, login } = useAuth();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Spinner size="lg" />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    // Trigger login flow
    login();
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <Spinner size="lg" />
        <p>Redirecting to login...</p>
      </div>
    );
  }

  // Check admin requirement
  if (requireAdmin && !user?.isAdmin) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <h1>Access Denied</h1>
        <p>You do not have admin access to this page.</p>
        <p>Please contact an administrator.</p>
      </div>
    );
  }

  return <>{children}</>;
}
